"use client";

import * as React from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  rating: number;
  onRatingChange?: (rating: number) => void;
  readonly?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  spacingClassName?: string;
}

export default function StarRating({
  rating,
  onRatingChange,
  readonly = false,
  size = "md",
  className,
  spacingClassName
}: StarRatingProps) {
  const [hoveredRating, setHoveredRating] = React.useState<number | null>(null);
  
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5", 
    lg: "h-6 w-6"
  };

  const handleClick = (newRating: number) => {
    if (!readonly && onRatingChange) {
      onRatingChange(newRating);
    }
  };

  const handleMouseEnter = (newRating: number) => {
    if (!readonly) {
      setHoveredRating(newRating);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoveredRating(null);
    }
  };

  const displayRating = hoveredRating ?? rating;

  return (
    <div className={cn("inline-flex items-center align-middle", spacingClassName ?? "gap-[3px]", className)}>
      {[1, 2, 3, 4, 5].map((star) => {
        const fullStars = Math.floor(displayRating);
        const fraction = Math.max(0, Math.min(1, displayRating - fullStars));
        const isFull = star <= fullStars;
        const isPartial = !isFull && star === fullStars + 1 && fraction > 0;
        const fillPercent = isFull ? 100 : isPartial ? Math.round(fraction * 100) : 0;

        const StarContent = (
          <span className={cn("relative inline-block", sizeClasses[size])}>
            {/* Base star (empty/gray) */}
            <Star className={cn("h-full w-full", "fill-gray-300 text-gray-300")} />
            {/* Overlay star (yellow) clipped to percentage */}
            {fillPercent > 0 && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${fillPercent}%` }}
                aria-hidden="true"
              >
                <Star className="h-full w-full fill-yellow-400 text-yellow-400" />
              </span>
            )}
          </span>
        );

        if (readonly) {
          return (
            <span
              key={star}
              className="inline-flex"
              aria-hidden={star > Math.ceil(displayRating)}
            >
              {StarContent}
            </span>
          );
        }

        return (
          <button
            key={star}
            type="button"
            onClick={() => handleClick(star)}
            onMouseEnter={() => handleMouseEnter(star)}
            onMouseLeave={handleMouseLeave}
            className="transition-transform p-0 m-0 bg-transparent border-0 inline-flex hover:scale-110 cursor-pointer"
            aria-label={`${displayRating} din 5 stele`}
          >
            {StarContent}
          </button>
        );
      })}
    </div>
  );
}
